import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";

import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { toast, localizeMessage } from '../utils/toast.js';
// Quick-action buttons elsewhere in the app link here as
// /organization-activities?tab=posts|liked|saved, so the initial tab must be
// read from that query param instead of always defaulting to "all".
const getInitialTab = (search) => {
  const tab = new URLSearchParams(search).get("tab");
  return ["all", "posts", "liked", "saved"].includes(tab) ? tab : "all";
};

function OrganizationActivities() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(() => getInitialTab(location.search));

  const [activities, setActivities] = useState([]);
  const [posts, setPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, [activeTab]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");

      if (!token) {
        setError(t('orgActivities.noToken'));
        return;
      }

      let endpoint = "";
      switch (activeTab) {
        case "all":
          endpoint = "/api/user/activities";
          break;
        case "posts":
          endpoint = "/api/user/posts";
          break;
        case "liked":
          endpoint = "/api/posts/liked";
          break;
        case "saved":
          endpoint = "/api/posts/saved";
          break;
        default:
          endpoint = "/api/user/activities";
      }

      const response = await fetch(`${GREENWICH_BACKEND_URL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const data = await response.json();

      if (activeTab === "all") {
        setActivities(data.activities || []);
      }

      if (activeTab === "posts") {
        setPosts(data.posts || []);
      }

      if (activeTab === "liked") {
        setLikedPosts(data.posts || []);
      }

      if (activeTab === "saved") {
        setSavedPosts(data.posts || []);
      }

    } catch (e) {
      console.error("Error fetching data:", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ======================== DELETE ========================
  const handleDeletePost = async (postId) => {
    if (!window.confirm(t('orgActivities.confirmDeletePost'))) return;

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${GREENWICH_BACKEND_URL}/api/posts/${postId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error(t('orgActivities.deleteFailed'));

      setPosts(p => p.filter(post => post._id !== postId));
      fetchUserData();

    } catch (e) {
      toast(t('orgActivities.deleteError', { message: e.message }));
    }
  };

  // ======================== HELPERS ========================
  const formatTime = (date) => {
    return new Date(date).toLocaleString();
  };

  // ======================== ACTIVITY ========================
  const getActivityIcon = () => "📝";

  const getActivityText = () => t('orgActivities.orgCreatedPost');

  // ======================== RENDER ========================
  const renderPosts = (list, deletable = false) => (
    <div className="posts-grid">
      {list.length === 0 ? (
        <p className="empty-state">{t('orgActivities.noPostsYet')}</p>
      ) : (
        list.map(post => (
          <div className="post-card" key={post._id}>

            <div className="post-header">

              <img
                src={post.userId?.profileImage || "/nkarner/default-avatar.jpg"}
                className="post-avatar"
                alt={t('orgActivities.profileAlt')}
              />

              <div className="post-author">
                <span className="author-name">
                  {post.userId?.organizationName || t('orgActivities.organization')}
                </span>
                <span className="post-date">
                  {formatTime(post.createdAt)}
                </span>
              </div>

              {deletable && (
                <button
                  className="delete-post-btn"
                  onClick={() => handleDeletePost(post._id)}
                >
                  🗑
                </button>
              )}

            </div>

            {post.image && (
              <img
                src={post.image}
                alt={t('orgActivities.postAlt')}
                className="post-image"
              />
            )}

            <p className="post-content">{post.content}</p>

            <div className="post-stats">
              ❤️ {post.likes?.length || 0}
              💬 {post.comments?.length || 0}
            </div>

          </div>
        ))
      )}
    </div>
  );

  // ======================== MAIN ==========================
  const renderContent = () => {
    if (error) return <p className="error">{t('orgActivities.error', { message: localizeMessage(error) })}</p>;
    if (loading) return <p>{t('orgActivities.loadingTab', { tab: activeTab })}</p>;

    switch (activeTab) {
      case "all":
        return (
          <div className="activities-list">
            {activities.length === 0 ? (
              <p>{t('orgActivities.noActivityYet')}</p>
            ) : (
              activities.map(act => (
                <div key={act._id} className="activity-item">
                  <div>{getActivityIcon()}</div>
                  <div>
                    <p>{getActivityText(act)}</p>
                    <small>{formatTime(act.createdAt)}</small>
                  </div>
                </div>
              ))
            )}
          </div>
        );

      case "posts":
        return renderPosts(posts, true);

      case "liked":
        return renderPosts(likedPosts);

      case "saved":
        return renderPosts(savedPosts);

      default:
        return null;
    }
  };

  return (
    <div className="activities-page">

      <div className="page-header">
        <h1>{t('orgActivities.title')}</h1>
        <p>{t('orgActivities.subtitle')}</p>
      </div>

      <div className="tabs">
        <button className={activeTab === "all" ? "active" : ""}
                onClick={() => setActiveTab("all")}>
          {t('orgActivities.all')}
        </button>

        <button className={activeTab === "posts" ? "active" : ""}
                onClick={() => setActiveTab("posts")}>
          {t('orgActivities.posts')}
        </button>

        <button className={activeTab === "liked" ? "active" : ""}
                onClick={() => setActiveTab("liked")}>
          {t('orgActivities.liked')}
        </button>

        <button className={activeTab === "saved" ? "active" : ""}
                onClick={() => setActiveTab("saved")}>
          {t('orgActivities.saved')}
        </button>
      </div>

      <div className="activities-content">
        {renderContent()}
      </div>
    </div>
  );
}

export default OrganizationActivities;
